import { use } from 'react';
import { useTranslation } from 'react-i18next';
import { AddCircleOutlineRounded, Link } from '@mui/icons-material';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Constants from 'style/constants';
import BreadCrumbTooltip from 'shared/components/bread-crumb/bread-crumb-tooltip/bread-crumb-tooltip';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';

import { SystemStatus } from 'features/systems/domain/models/system-status';
import IPUnitLocationType from 'features/systems/ip-unit/domain/enums/ip-unit-location-type';
import IPUnitProviderContext from 'features/systems/ip-unit/providers/contexts/ip-unit-provider-context';
import styles from './ip-unit-location.module.scss';

const IPUnitLocation = (): React.JSX.Element => {
  const { t } = useTranslation('ipUnit');
  const { hasPermission } = usePermissionAuthorization();
  const {
    viewingMode,
    selectedSystem,
    openDeviceDetailsByExternalId,
    unitLocationHasLinkedDevice,
    getUnitLocationDeviceBreadCrumbs,
    unitLocationHasDevice,
    createIPUnitDevice,
  } = use(IPUnitProviderContext);

  const textSx: SxProps<Theme> = {
    '&.Mui-disabled': {
      color: Constants.Colors.primary,
      opacity: 0.38,
    },
  };

  const getExternalId = (locationType: IPUnitLocationType, index: number): string => {
    if (locationType === IPUnitLocationType.Room) {
      return locationType;
    }

    return `${locationType}${index + 1}`;
  };

  const renderIPUnitLocations = (ipUnitLocationType: IPUnitLocationType, numberOfLocations: number): React.JSX.Element => {
    return (
      <>
        {Array.from({ length: numberOfLocations }, (_, index) => (
          <Stack key={`${ipUnitLocationType + index}`} direction="row" className={styles.ipUnitLocationWrapper}>
            <Stack direction="row" className={styles.roomIconWrapper}>
              <Typography
                className={`${styles.location} ${(unitLocationHasDevice(getExternalId(ipUnitLocationType, index)) || ipUnitLocationType === IPUnitLocationType.Room) && viewingMode === 'viewing' ? styles.clickable : ''} `}
                variant="subtitle1"
                onClick={() => openDeviceDetailsByExternalId(getExternalId(ipUnitLocationType, index))}
              >
                {t(`locations.${ipUnitLocationType}`, { index: index + 1 })}
              </Typography>
              {unitLocationHasLinkedDevice(getExternalId(ipUnitLocationType, index)) && (
                <BreadCrumbTooltip allElementNames={getUnitLocationDeviceBreadCrumbs(getExternalId(ipUnitLocationType, index))}>
                  <Link className={styles.linkIcon} />
                </BreadCrumbTooltip>
              )}
            </Stack>
            {hasPermission(Permission.CreateDevice) && !unitLocationHasDevice(getExternalId(ipUnitLocationType, index)) && (
              <Button
                startIcon={<AddCircleOutlineRounded />}
                sx={textSx}
                variant="text"
                onClick={() =>
                  createIPUnitDevice({
                    systemId: selectedSystem?.id ?? '',
                    location: ipUnitLocationType,
                    number: ipUnitLocationType === IPUnitLocationType.Room ? undefined : index + 1,
                    isSubUnit: false,
                  })
                }
                onFocus={(event) => event.stopPropagation()}
                disabled={viewingMode === 'editing' || selectedSystem?.status === SystemStatus.offline}
                className={styles.unlinkButton}
              >
                {t('locations.createDevice')}
              </Button>
            )}
          </Stack>
        ))}
      </>
    );
  };

  return (
    <div className={styles.ipUnitLocationContainer}>
      <Typography className={styles.locationsTitle} variant="h5">
        {t('locationsTitle')}
      </Typography>

      <Stack direction="column">
        {renderIPUnitLocations(IPUnitLocationType.Room, 1)}
        {renderIPUnitLocations(IPUnitLocationType.Bed, 8)}
        {renderIPUnitLocations(IPUnitLocationType.Toilet, 8)}
      </Stack>
    </div>
  );
};

export default IPUnitLocation;
